/**
 * SHOWN OR KEPT - how a GM's document says who may read it.
 *
 * A clock and a board entry are each one document the GM writes. Who sees
 * it is the document's `audience` column, which the rules read: `['*']`
 * for everyone, `['@players']` for every player, account ids for named
 * people, `[]` for the GM alone (v9.5.9; before, shown was `['*']` and
 * kept `[]`, nothing between). The `visible` flag inside the payload is
 * the same fact for the screen - "somebody besides the GM sees it" - and
 * `shownDoc` is the one place both are written, so they cannot disagree.
 * A stored payload with no audience reads its old `visible` as `['*']`.
 *
 * Pure, zero React dependency.
 */
import { TO_ALL } from '../chat/types';

export interface Shown {
  id: string;
  /** Who sees it besides the GM: `['*']`, `['@players']`, account ids; `[]` = the GM alone. */
  audience: string[];
  /** Derived: somebody besides the GM sees it. Kept on the wire for older readers. */
  visible: boolean;
  /** When it was made, in ms - the order things are listed in. */
  createdAt: number;
}

/** The audience, from the explicit list or from the plain switch: shown to everyone, or kept. */
export function audienceOf(patch: { audience?: string[]; visible?: boolean }, had: string[]): string[] {
  if (patch.audience !== undefined) return [...new Set(patch.audience.filter((a) => typeof a === 'string' && a))];
  if (patch.visible !== undefined) return patch.visible ? [TO_ALL] : [];
  return had;
}

/** The document as stored: the payload as text (never json - see the schema), and who may read it. */
export function shownDoc(item: Shown): { data: string; audience: string[] } {
  return { data: JSON.stringify({ ...item, visible: item.audience.length > 0 }), audience: item.audience };
}

/** Oldest first; the id breaks a tie so every screen lists them alike. */
export function byCreation<T extends Shown>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
}

/** The fields every shown thing carries, read off a stored payload. */
export function readShownBase(id: string, stored: Record<string, unknown>): Shown {
  const audience = Array.isArray(stored.audience)
    ? stored.audience.filter((a): a is string => typeof a === 'string' && a !== '')
    : stored.visible === true
      ? [TO_ALL]
      : [];
  return {
    id,
    audience,
    visible: audience.length > 0,
    createdAt: typeof stored.createdAt === 'number' && Number.isFinite(stored.createdAt) ? stored.createdAt : 0,
  };
}
